import {
  BackComponent,
  CheckBox,
  ContactExpert,
  FormInput,
  FormPhone,
  PageMainContent,
  PageWidgetContainer,
  SuccessView,
  Typography,
} from '@/components'
import { ThemeColor } from '@/enums'
import { useSteps } from '@/pages/Profile/helpers'
import {
  ButtonWrapper,
  CheckboxWrapper,
  PageHeader,
  ReferralWrapper,
} from '@/pages/Profile/styles'
import { FormProvider } from '@/providers'
import { paths } from '@/routes/paths'
import { useSendMessage } from '@/services'
import { useGoToPath, useScrollTop } from '@/utils'
import { Button } from '@surein/ui'
import { keys } from 'ramda'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { validationSchema } from './validators'

interface ReferralFormValues {
  company: string
  Owner: string
  Email: string
  Phone: string
}

interface ReferralMessages {
  referral: {
    title: string
    subtitle: string
    company: {
      label: string
    }
    contactPerson: {
      label: string
    }
    contactEmail: {
      label: string
    }
    contactPhone: {
      label: string
    }
    iConfirm: string
    btn: string
    referalCondition: string
    success: {
      title: string
      subtitle: string
      btn: string
    }
  }
  common: string
  validations: {
    company: string
    email: string
    general: string
    phone: string
  }
  advisorText: string
}

const useMessages = (): ReferralMessages => {
  const { t } = useTranslation()

  return {
    referral: t(`profile.referral`, { returnObjects: true }),
    common: t('common'),
    validations: t('validations', { returnObjects: true }),
    advisorText: t('recommend_advisor_text'),
  }
}

export const ReferralPage = () => {
  const goToDashboard = useGoToPath(paths.dashboard)
  const messages = useMessages()
  const { nextStep, isActive } = useSteps(1, 2)
  const createmutation = useSendMessage()
  const formText = (obj: ReferralFormValues) =>
    keys(obj)
      .map((k) => `${k}: "${obj[k]}"`)
      .join(', ')
  const { scrollTop } = useScrollTop()
  const onFormSubmit = (values: ReferralFormValues) => {
    createmutation
      .mutateAsync(`referral program request for ${formText(values)}`)
      .then(() => {
        nextStep()
        scrollTop()
      })
  }
  const [iAgree, setIAgree] = useState(false)
  const toggleIAgree = () => setIAgree(!iAgree)

  return (
    <>
      <PageMainContent>
        <BackComponent />
        {isActive(1) ? (
          <ReferralWrapper>
            <PageHeader>
              <Typography variant="h4">{messages.referral.title}</Typography>
              <Typography color={ThemeColor.b50} variant="p1Body">
                {messages.referral.subtitle}
              </Typography>
            </PageHeader>
            <FormProvider
              onSubmit={onFormSubmit}
              validationSchema={validationSchema(messages.validations)}
            >
              <FormInput
                label={messages.referral.company.label}
                name="company"
              />
              <FormInput
                label={messages.referral.contactPerson.label}
                name="Owner"
              />
              <FormInput
                label={messages.referral.contactEmail.label}
                name="Email"
              />
              <FormPhone
                label={messages.referral.contactPhone.label}
                name="Phone"
              />
              <CheckboxWrapper>
                <CheckBox
                  checked={iAgree}
                  label={messages.referral.iConfirm}
                  onChange={toggleIAgree}
                />
              </CheckboxWrapper>
              <ButtonWrapper>
                <Button
                  disabled={!iAgree}
                  type="submit"
                  variant="primary"
                  shape="square"
                >
                  {messages.referral.btn}
                </Button>
              </ButtonWrapper>
            </FormProvider>
            <Typography color={ThemeColor.b50} variant="p2Body">
              {messages.referral.referalCondition}
            </Typography>
          </ReferralWrapper>
        ) : (
          <SuccessView
            buttonText={messages.referral.success.btn}
            onButtonClick={goToDashboard}
            subtitle={messages.referral.success.subtitle}
            title={messages.referral.success.title}
          />
        )}
      </PageMainContent>
      <PageWidgetContainer>
        <ContactExpert text={messages.advisorText} />
      </PageWidgetContainer>
    </>
  )
}
